/**
 * AWNINGS (2026-10-08) — the sizing rules, in ONE place (docs/bim-plan/25-awning-study.md §3–§4).
 * The geometry (bim-geometry categories/awning), the palette's warnings and the cut list all
 * read these, so a hood is drawn, checked and counted from the same numbers.
 *
 * Every rule here came out of the study: the bracket proportions (Fine Homebuilding), the
 * minimum pitch per roofing (CRC R905), the bracket spacing (6'-0" max, 8'-0" heavy timber,
 * never in front of glass — the beam upsizes instead), the fabric rules (projection = drop,
 * an odd stripe count so both edges end on a full dark bar), the rod angle (≥ 45°).
 */
import type { ResolvedElement } from './params'
import { resolveOpeningFrame } from './openingFrame'
import { GUTTER_PROFILES, GUTTER_SIZES } from './roofFraming'
import { resolveExteriorTrimStyle, resolveExteriorTrimSizes } from './exteriorTrim'

const IN = 1 / 12

/** the awning fabric — solution-dyed acrylic; the stripe colors are the type's */
export const AWNING_FABRIC_MATERIAL_NAME = 'Awning Fabric — Solution-Dyed Acrylic'

export type AwningForm = 'shed' | 'gable' | 'concealed' | 'rod' | 'fabric' | 'portico'
export const AWNING_FORMS: { key: AwningForm; label: string }[] = [
  { key: 'shed', label: 'Shed Hood on Brackets' },
  { key: 'gable', label: 'Gabled Hood (open truss)' },
  { key: 'concealed', label: 'Concealed Hood (no visible bracket)' },
  { key: 'rod', label: 'Rod Canopy (steel)' },
  { key: 'fabric', label: 'Fabric Awning' },
  { key: 'portico', label: 'Entry Portico (two posts)' },
]

/** the hood each firm style starts from (docs/bim-plan/25 §5) — the Awning tool picks it when
 *  the project follows that style; any type can still be picked */
export const AWNING_STYLE_DEFAULTS: Record<string, string> = {
  'California Modern': 'Rod Canopy — Steel',
  'Modern Spanish': 'Shed Hood — Timber Corbel, Barrel Tile',
  'Modern Farmhouse': 'Shed Hood — Knee Brace, Standing Seam',
  'Mountain Modern': 'Shed Hood — Heavy Timber, Slate',
  'Organic Modern': 'Concealed Hood — Standing Seam',
}

export type AwningBracket = 'knee' | 'solid' | 'curved' | 'timber' | 'strut'
export const AWNING_BRACKETS: { key: AwningBracket; label: string }[] = [
  { key: 'knee', label: 'Knee Brace (leg, arm, 45° brace)' },
  { key: 'solid', label: 'Solid Sawn (cove cut)' },
  { key: 'curved', label: 'Curved Knee' },
  { key: 'timber', label: 'Heavy Timber (chamfered, square washers)' },
  { key: 'strut', label: 'Steel Strut' },
]

/** two openings closer than this (casing edge to casing edge) read as one mulled / ganged line */
export const AWNING_MULL_GAP_FT = 3 * IN

/** dressed lumber, inches: [thickness, width] */
export const AWNING_LUMBER: Record<string, [number, number]> = {
  '2x4': [1.5, 3.5], '2x6': [1.5, 5.5], '2x8': [1.5, 7.25], '2x10': [1.5, 9.25], '2x12': [1.5, 11.25],
  '3x6': [2.5, 5.5], '4x4': [3.5, 3.5], '4x6': [3.5, 5.5], '4x8': [3.5, 7.25], '4x10': [3.5, 9.25],
  '6x6': [5.5, 5.5], '6x8': [5.5, 7.5], '6x10': [5.5, 9.5], '8x8': [7.5, 7.5],
}
export const lumberFt = (key: string): [number, number] => {
  const m = AWNING_LUMBER[key] ?? AWNING_LUMBER['4x4']
  return [m[0] * IN, m[1] * IN]
}

/** the lowest pitch each roofing profile may sit at (CRC R905), rise per 12 */
export function minPitchFor(profile: string | null | undefined, shingleStyle?: string | null): number {
  if (profile === 'standingSeam') return 0.25
  if (profile === 'barrel') return 3
  if (profile === 'shingle') return shingleStyle === 'slate' ? 4 : shingleStyle === 'shake' ? 3 : 2
  return 0.25
}

/** the bracket members for one type, feet: in-plane depth of leg/arm, brace width, out-of-plane thickness */
export interface BracketStock { legW: number; armD: number; braceW: number; thick: number; post: boolean }
export function bracketStock(kind: AwningBracket, size: string): BracketStock {
  if (kind === 'timber') return { legW: 7.5 * IN, armD: 7.5 * IN, braceW: 5.5 * IN, thick: 5.5 * IN, post: false }
  if (kind === 'solid') return { legW: 3.5 * IN, armD: 3.5 * IN, braceW: 0, thick: 3 * IN, post: false }
  if (kind === 'strut') return { legW: 0, armD: 5.5 * IN, braceW: 2 * IN, thick: 3.5 * IN, post: false }
  const [t, w] = lumberFt(size)
  return { legW: w, armD: w, braceW: w, thick: t, post: false }
}

/**
 * ONE sizing rule for every bracket (the study's Plate 2): the brace sits at 45° and lands
 * about 60% out the arm, but always inboard of the beam and of the arm's shaped end; the leg
 * is never shorter than the arm. `braceMax` = where the beam's inner face is (the brace must
 * land before it). Returns feet.
 */
export function bracketGeometry(kind: AwningBracket, size: string, armLen: number, braceMax: number) {
  const s = bracketStock(kind, size)
  const profRun = Math.min(kind === 'timber' ? 7 * IN : 6 * IN, armLen * 0.3)
  let a = Math.min(armLen * 0.6, braceMax - s.legW - IN, armLen - profRun - 1.5 * IN - s.legW - s.braceW * Math.SQRT2)
  a = Math.max(a, 4 * IN)
  const legLen = Math.max(armLen * 1.05, a + s.legW * 1.6 + s.braceW * 1.5 + 4 * IN)
  return { ...s, profRun, a, legLen }
}

/** bracket stations along the hood (s, feet): just outside each opening's casing, merged when
 *  closer than 2 ft, with even extra brackets where a bay runs past the max spacing — except an
 *  extra bracket never lands in front of an opening; that bay's beam upsizes instead. */
export function bracketStations(
  openings: { s0: number; s1: number }[], hoodS0: number, hoodS1: number, thick: number, maxSpacing: number,
): { stations: number[]; longBay: number } {
  // a MULLED line is one opening to a bracket: units whose casings touch (or come within the mull
  // gap) merge first, so no bracket ever lands on a shared frame between them (Matt, 2026-10-08)
  const runs: { s0: number; s1: number }[] = []
  for (const o of [...openings].sort((a, b) => a.s0 - b.s0)) {
    const last = runs[runs.length - 1]
    if (last && o.s0 <= last.s1 + AWNING_MULL_GAP_FT) last.s1 = Math.max(last.s1, o.s1)
    else runs.push({ s0: o.s0, s1: o.s1 })
  }
  openings = runs
  const xs: number[] = []
  for (const o of openings) xs.push(o.s0 - thick / 2 - 0.5 * IN, o.s1 + thick / 2 + 0.5 * IN)
  if (!xs.length) xs.push(hoodS0 + 6 * IN + thick / 2, hoodS1 - 6 * IN - thick / 2)
  xs.sort((a, b) => a - b)
  const merged: number[] = []
  for (const x of xs) {
    if (merged.length && x - merged[merged.length - 1] < 2) merged[merged.length - 1] = (merged[merged.length - 1] + x) / 2
    else merged.push(x)
  }
  const over = (x: number) => openings.some((o) => x > o.s0 - thick && x < o.s1 + thick)
  const out = [merged[0]]
  let longBay = 0
  for (let i = 1; i < merged.length; i++) {
    const a0 = merged[i - 1], a1 = merged[i], n = Math.ceil((a1 - a0) / maxSpacing - 1e-9)
    for (let k = 1; k < n; k++) {
      const x = a0 + ((a1 - a0) * k) / n
      if (over(x)) longBay = Math.max(longBay, a1 - a0)
      else out.push(x)
    }
    out.push(a1)
  }
  out.sort((a, b) => a - b)
  return { stations: out.filter((x) => x > hoodS0 + 1e-6 && x < hoodS1 - 1e-6), longBay }
}

/** the beam that carries the rafter ends between brackets: 4×6, a 4×8 when a bay can't take a
 *  bracket over the glass and runs long, 6×8 under heavy timber */
export function beamSizeFor(kind: AwningBracket, longBay: number, maxSpacing: number): string {
  if (kind === 'timber') return '6x8'
  return longBay > maxSpacing + 1e-6 ? '4x8' : '4x6'
}

/** fabric: an ODD number of stripes so the awning is symmetric and ends on full stripes */
export function fabricWidthSnapped(widthFt: number, stripeFt: number): number {
  if (!(stripeFt > 0.01)) return widthFt
  let n = Math.round(widthFt / stripeFt)
  if (n % 2 === 0) n += 1
  return Math.max(3, n) * stripeFt
}

/** the engineering + code flags the tool raises (plain words) */
export function awningWarnings(o: {
  form: AwningForm; projectionFt: number; longBay: number; maxSpacing: number; rodAngleDeg: number
  pitch: number; minPitch: number; roofingName?: string; fireBanned?: boolean
}): string[] {
  const w: string[] = []
  if (o.form !== 'fabric' && o.form !== 'portico' && o.projectionFt > 4 + 1e-6) w.push('Past 4\'-0" of projection this hood gets engineered.')
  if (o.longBay > o.maxSpacing + 1e-6) w.push(`No bracket can land in front of the opening, so the beam goes up a size to span ${Math.round(o.longBay * 12)}".`)
  if (o.form === 'rod' && o.rodAngleDeg < 45) w.push('The rods are flatter than 45°. The rod force grows fast below that; the engineer sizes the clevises and wall plate.')
  if (o.pitch + 1e-6 < o.minPitch) w.push(`${o.roofingName || 'This roofing'} needs at least ${o.minPitch}:12. The hood is built at ${o.minPitch}:12.`)
  if (o.fireBanned) w.push('Wood shingle and shake roofing is not allowed in a Very High Fire Hazard Severity Zone.')
  return w
}

// ---------------------------------------------------------------------------------------------
// THE LAYOUT — resolved once per awning (doc.ts), read by the geometry, snapping, the palette,
// the roof plan and the cut list. Stations run along the host wall's flattened path (the frame
// its openings use); z is absolute.
// ---------------------------------------------------------------------------------------------

export interface AwningRoofingInfo { profile: string; shingleStyle?: string | null; name?: string; fireBanned?: boolean }

export interface AwningSpec {
  form: AwningForm
  porticoRoof: 'gable' | 'shed'
  bracket: AwningBracket
  bracketSize: string
  armEnd: string
  tails: boolean
  tailStyle: string
  gutter: boolean
  /** the PROJECT's gutter (Project Settings → Trim & Eaves, the same one every roof hangs) */
  gutterProfile: string
  gutterSize: string
  gutterWidthIn: number
  gutterHeightIn: number
  downspouts: boolean
  /** every door / window the hood covers — the one it was placed on plus its mulled line */
  openingIds: string[]
  trellis: boolean
  stained: boolean
  /** +1 = the hood stands off the host path's LEFT side (the niche convention) */
  sign: 1 | -1
  /** the hood's span along the wall */
  s0: number
  s1: number
  /** the openings it covers, casing included (brackets land just outside these) */
  covers: { s0: number; s1: number }[]
  /** absolute z of the hood's underside at the front (fascia bottom / tail tips / valance line) */
  zUnder: number
  /** the floor of the awning's level (a portico stands its posts here) */
  zFloor: number
  projectionFt: number
  /** rise per 12, already raised to the roofing's minimum */
  pitch: number
  minPitch: number
  /** bracket / post / rod / spear-arm stations along the wall */
  stations: number[]
  longBay: number
  maxSpacingFt: number
  beam: string
  dropFt: number
  valanceFt: number
  stripeFt: number
  valanceEdge: 'straight' | 'scallop'
  sidesClosed: boolean
  stripeA: string
  stripeB: string
  postSize: string
  rodAngleDeg: number
  warnings: string[]
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const str = (v: unknown, d: string): string => (typeof v === 'string' && v ? v : d)

/** an opening's exterior casing: its width each side, and how far the head trim rises */
function casingOf(opening: ResolvedElement): { side: number; head: number } {
  const merged = { ...opening.typeParams, ...opening.params }
  const style = resolveExteriorTrimStyle(merged)
  const sz = resolveExteriorTrimSizes(merged)
  switch (style) {
    case 'traditional': return { side: sz.trimWidthFt, head: sz.trimHeadFt }
    case 'pictureFrame': return { side: sz.trimWidthFt, head: sz.trimWidthFt }
    case 'modern': return { side: sz.tubeThickFt, head: sz.tubeThickFt }
    case 'brick': return { side: sz.lintelEndsFt, head: 7.625 * IN }
    default: return { side: 0, head: 0 }
  }
}

/** the opening's rough span + head (absolute) — the numbers the wall cuts it with */
function openingSpan(opening: ResolvedElement): { s0: number; s1: number; zHead: number } {
  const p = opening.params
  const w = num(p.widthFt) ?? 3
  const fw = resolveOpeningFrame(opening.category, { ...opening.typeParams, ...p }).widthFt
  let total = w + 2 * fw
  if (opening.category === 'door') {
    if (p.sidelightLeft === true) total += (num(p.sidelightLeftWidthFt) ?? 1.25) + fw
    if (p.sidelightRight === true) total += (num(p.sidelightRightWidthFt) ?? 1.25) + fw
  }
  const c = num(p.hostOffsetFt) ?? 0
  const zBase = opening.level?.elevationFt ?? 0
  const transom = p.transom === true ? (num(p.transomHeightFt) ?? 1) : 0
  // SHAPED HEADS (2026-10-30): the crown — an arch's rise (held to half the span, as the wall
  // cuts it), or a rake's full rise (slope x span, the project slope when the window has none);
  // a raked head used to read the unused arch-rise field here
  const head = String(p.headType || 'flat')
  const rakeSlope = num(p.rakeSlopeRise) ?? opening.windowRake?.slopeRise ?? 6
  const rise = head === 'halfRound' ? total / 2
    : head === 'arched' || head === 'segmental' ? Math.min(num(p.headRiseFt) ?? 0, total / 2)
    : head === 'raked' ? Math.min((rakeSlope / 12) * total, total) : 0
  return { s0: c - total / 2, s1: c + total / 2, zHead: zBase + (num(p.sillFt) ?? 0) + (num(p.heightFt) ?? 6.667) + transom + rise }
}

/** what the model adds around one awning: the host's other doors and windows (a mulled line is
 *  covered whole) and the project's gutter standard */
export interface AwningContext {
  siblings?: ResolvedElement[]
  gutterProfile?: string | null
  gutterSize?: string | null
}

/** the opening plus every door / window mulled to it on the same wall (casings touching or within
 *  AWNING_MULL_GAP_FT, chained), in station order */
export function awningMulledLine(opening: ResolvedElement, siblings: ResolvedElement[]): ResolvedElement[] {
  const span = (o: ResolvedElement) => { const sp = openingSpan(o), c = casingOf(o); return { s0: sp.s0 - c.side, s1: sp.s1 + c.side } }
  const pool = siblings.filter((o) => o.id !== opening.id && (o.category === 'door' || o.category === 'window') && o.hostId === opening.hostId)
  const group = [opening]
  let lo = span(opening).s0, hi = span(opening).s1
  let grew = true
  while (grew) {
    grew = false
    for (const o of pool) {
      if (group.includes(o)) continue
      const r = span(o)
      if (r.s0 <= hi + AWNING_MULL_GAP_FT && r.s1 >= lo - AWNING_MULL_GAP_FT) {
        group.push(o); lo = Math.min(lo, r.s0); hi = Math.max(hi, r.s1); grew = true
      }
    }
  }
  return group.sort((a, b) => (num(a.params.hostOffsetFt) ?? 0) - (num(b.params.hostOffsetFt) ?? 0))
}

export function awningSpec(
  el: ResolvedElement, host: ResolvedElement | null, opening: ResolvedElement | null, roofing: AwningRoofingInfo,
  ctx: AwningContext = {},
): AwningSpec {
  const p = el.params, tp = el.typeParams
  const form = (AWNING_FORMS.some((f) => f.key === tp.form) ? tp.form : 'shed') as AwningForm
  const bracket = (AWNING_BRACKETS.some((b) => b.key === tp.bracket) ? tp.bracket : 'knee') as AwningBracket
  const flipped = host?.params.flipped === true
  const face = p.face === 'interior' ? 'interior' : 'exterior'
  const sign: 1 | -1 = (face === 'exterior') !== flipped ? 1 : -1
  const zFloor = el.level?.elevationFt ?? host?.level?.elevationFt ?? 0
  const fitOpening = p.fit !== 'manual' && !!opening
  const margin = num(p.marginFt) ?? 6 * IN
  const clearance = num(p.clearanceFt) ?? 6 * IN

  let s0: number, s1: number, zUnder: number
  const covers: { s0: number; s1: number }[] = []
  const manual = () => { const c = num(p.hostOffsetFt) ?? 0, w = num(p.widthFt) ?? 6; s0 = c - w / 2; s1 = c + w / 2 }
  const line = opening ? awningMulledLine(opening, ctx.siblings ?? []) : []
  if (opening) {
    // the whole mulled line: its outer casings, its tallest head
    let lo = Infinity, hi = -Infinity, headTop = -Infinity
    for (const o of line) {
      const sp = openingSpan(o)
      const cas = casingOf(o)
      covers.push({ s0: sp.s0 - cas.side, s1: sp.s1 + cas.side })
      lo = Math.min(lo, sp.s0 - cas.side); hi = Math.max(hi, sp.s1 + cas.side)
      headTop = Math.max(headTop, sp.zHead + cas.head)
    }
    if (fitOpening) { s0 = lo - margin; s1 = hi + margin } else manual()
    zUnder = p.heightMode === 'level' ? zFloor + (num(p.bottomFt) ?? 8.5) : headTop + clearance
  } else {
    manual()
    zUnder = zFloor + (num(p.bottomFt) ?? 8.5)
  }
  // a portico wants room beside the door for its posts
  if (form === 'portico' && fitOpening) { s0! -= 6 * IN; s1! += 6 * IN }

  const projectionFt = num(p.projectionFt) ?? num(tp.projectionFt) ?? 32 * IN
  const trellis = tp.trellis === true && (form === 'shed' || form === 'gable' || form === 'portico')
  const minPitch = form === 'fabric' || trellis ? 0 : minPitchFor(roofing.profile, roofing.shingleStyle)
  const asked = num(p.slopeRise) ?? num(tp.slopeRise) ?? 4
  const pitch = Math.max(asked, minPitch)

  // fabric: the width snaps to an odd count of stripes, centered (full dark bars at both edges)
  const stripeFt = num(tp.stripeFt) ?? 4.625 * IN
  if (form === 'fabric' && stripeFt > 0.01) {
    const w = fabricWidthSnapped(s1! - s0!, stripeFt), c = (s0! + s1!) / 2
    s0 = c - w / 2; s1 = c + w / 2
  }

  const bracketSize = str(tp.bracketSize, '4x4')
  const stock = bracketStock(bracket, bracketSize)
  const maxSpacingFt = bracket === 'timber' ? 8 : 6
  let stations: number[] = []
  let longBay = 0
  if (form === 'shed') {
    const r = bracketStations(covers, s0!, s1!, stock.thick, maxSpacingFt)
    stations = r.stations; longBay = r.longBay
  } else if (form === 'gable') {
    stations = [s0! + 3.25 * IN, s1! - 3.25 * IN]
  } else if (form === 'portico') {
    const post = lumberFt(str(tp.postSize, '6x6'))[1]
    stations = [s0! + post / 2 + 3 * IN, s1! - post / 2 - 3 * IN]
  } else if (form === 'rod' || form === 'fabric') {
    const span = s1! - s0!, inset = form === 'rod' ? 6 * IN : 10 * IN
    const n = Math.max(2, Math.ceil((span - 2 * inset) / (form === 'rod' ? 5 : 4)) + 1)
    for (let i = 0; i < n; i++) stations.push(s0! + inset + ((span - 2 * inset) * i) / (n - 1))
  }
  const beam = beamSizeFor(bracket, longBay, maxSpacingFt)
  // GUTTERS: the instance turns them on or off (seeded from the type at placement); profile and
  // size are the project's (the roofs' own standard), never the awning's
  const gutterOn = (typeof p.gutters === 'boolean' ? p.gutters : tp.gutter !== false) && form !== 'fabric' && !trellis
  const gProfile = GUTTER_PROFILES.some((g) => g.key === ctx.gutterProfile) ? ctx.gutterProfile as string : 'halfRound'
  const gSize = GUTTER_SIZES.find((g) => g.key === ctx.gutterSize) ?? GUTTER_SIZES[0]
  const rodAngleDeg = num(tp.rodAngleDeg) ?? 45

  const warnings = awningWarnings({
    form, projectionFt, longBay, maxSpacing: maxSpacingFt, rodAngleDeg, pitch: asked, minPitch,
    roofingName: roofing.name, fireBanned: !!roofing.fireBanned && !trellis && form !== 'fabric',
  })
  if (p.fit !== 'manual' && !opening) warnings.push('Not over a door or window — place it on one, or set its width.')

  return {
    form,
    porticoRoof: tp.porticoRoof === 'shed' ? 'shed' : 'gable',
    bracket, bracketSize,
    armEnd: str(tp.armEnd, 's14'),
    tails: tp.tails === true && (form === 'shed' || form === 'gable' || form === 'portico'),
    tailStyle: str(tp.tailStyle, 's15'),
    gutter: gutterOn,
    gutterProfile: gProfile,
    gutterSize: gSize.key,
    gutterWidthIn: gSize.widthIn,
    gutterHeightIn: gSize.heightIn,
    downspouts: gutterOn && p.downspouts !== false,
    openingIds: line.map((o) => o.id),
    trellis,
    stained: tp.stained === true || bracket === 'timber',
    sign, s0: s0!, s1: s1!, covers, zUnder: zUnder!, zFloor, projectionFt, pitch, minPitch,
    stations, longBay, maxSpacingFt, beam,
    dropFt: num(p.dropFt) ?? projectionFt,
    valanceFt: num(tp.valanceFt) ?? 9 * IN,
    stripeFt,
    valanceEdge: tp.valance === 'scallop' ? 'scallop' : 'straight',
    sidesClosed: tp.fabricSides === 'closed',
    stripeA: str(tp.stripeA, '#1f2426'),
    stripeB: str(tp.stripeB, '#f2efe6'),
    postSize: str(tp.postSize, '6x6'),
    rodAngleDeg,
    warnings,
  }
}

/**
 * The framer's cut list (the study's "five-minute test" table): every member, its size and its
 * count/length, from the same layout the geometry builds. Lengths in feet (callers format).
 */
export interface AwningCutRow { part: string; size: string; qty: number; lengthFt?: number; note?: string }
/** e.g. 5″ half-round — the project gutter, as the cut list names it */
function gutterLabel(sp: AwningSpec): string {
  return `${sp.gutterWidthIn}″ ${GUTTER_PROFILES.find((g) => g.key === sp.gutterProfile)?.label.toLowerCase() ?? 'half-round'}`
}

export function awningCutList(sp: AwningSpec, roofingName?: string): AwningCutRow[] {
  const W = sp.s1 - sp.s0, P = sp.projectionFt, m = sp.pitch / 12, slope = Math.sqrt(1 + m * m)
  const rows: AwningCutRow[] = []
  const nRafters = (span: number, sp2: number, t: number) => Math.max(2, Math.round((span - t) / sp2) + 1)
  const roofRow = (area: number) => {
    if (sp.trellis) rows.push({ part: 'Trellis slats', size: '2×2 cedar @ 4″', qty: Math.max(1, Math.floor(P / (4 * IN))), lengthFt: W })
    else rows.push({ part: 'Roofing', size: roofingName || 'Standing seam', qty: 1, note: `${area.toFixed(1)} SF` })
  }
  if (sp.form === 'fabric') {
    const tubeLF = 2 * W + (sp.stations.length + 2) * Math.hypot(P, sp.dropFt - sp.valanceFt) + sp.stations.length * Math.hypot(P, sp.dropFt)
    rows.push({ part: 'Frame', size: '1″ sq. aluminum, welded', qty: 1, note: `${tubeLF.toFixed(1)} LF of tube` })
    rows.push({ part: 'Spear arms', size: '3/4″ tube', qty: sp.stations.length })
    const yd = ((Math.hypot(P, sp.dropFt - sp.valanceFt) + sp.valanceFt + 0.5) * Math.ceil(W / (46 * IN)) * (46 * IN)) / 9
    rows.push({ part: 'Fabric', size: '46″ solution-dyed acrylic', qty: 1, note: `about ${Math.ceil(yd * 1.15)} yd with waste` })
    rows.push({ part: 'Wall clips', size: '1/4″ SS lags', qty: Math.ceil(W / 2) + 1 })
    return rows
  }
  if (sp.form === 'rod') {
    rows.push({ part: 'Fascia channel', size: 'C8', qty: 1, lengthFt: W })
    rows.push({ part: 'Outriggers', size: 'HSS 2×6', qty: sp.stations.length, lengthFt: P })
    rows.push({ part: 'Rods', size: '3/4″ 316 SS, forged clevises', qty: sp.stations.length, lengthFt: P / Math.cos((sp.rodAngleDeg * Math.PI) / 180) })
    rows.push({ part: 'Turnbuckles', size: '3/4″ SS', qty: sp.stations.length })
    rows.push({ part: 'Gusset plates', size: '3/8″ steel, thru-bolted', qty: sp.stations.length })
    rows.push({ part: 'Soffit', size: '1×4 T&G cedar', qty: 1, note: `${(W * P).toFixed(1)} SF` })
    roofRow(W * P * slope)
    return rows
  }
  const gable = sp.form === 'gable' || (sp.form === 'portico' && sp.porticoRoof === 'gable')
  if (gable) {
    const hW = W / 2, rafterLen = (hW + (sp.tails ? 10 : 6) * IN) * slope
    rows.push({ part: 'Eave plates', size: '4×6', qty: 2, lengthFt: P })
    rows.push({ part: 'Tie beam', size: '4×8', qty: 1, lengthFt: W })
    rows.push({ part: 'Collar', size: '4×6', qty: 1, lengthFt: hW })
    rows.push({ part: 'Diagonals', size: '4×4, half-lapped', qty: 2, lengthFt: Math.hypot(hW, hW * m) })
    rows.push({ part: 'Gable rafters', size: '4×8', qty: 2, lengthFt: hW * slope })
    rows.push({ part: 'Rafters', size: sp.tails ? '3×6' : '2×6', qty: 2 * nRafters(P, sp.tails ? 2 : 16 * IN, 1.5 * IN), lengthFt: rafterLen })
    rows.push({ part: 'Ridge + wall ledger', size: '2×10 / 2×8', qty: 2, lengthFt: P })
    rows.push({ part: 'Barge boards', size: '2×10', qty: 2, lengthFt: rafterLen })
    if (!sp.trellis) rows.push({ part: 'Decking', size: '1×6 T&G cedar', qty: 1, note: `${(2 * rafterLen * P).toFixed(1)} SF` })
    roofRow(2 * rafterLen * (P + 0.5))
    if (sp.gutter) rows.push({ part: 'Gutters', size: gutterLabel(sp), qty: 2, lengthFt: P + 0.5 })
  } else {
    const raf = sp.bracket === 'timber' && sp.form !== 'portico' ? '4×6' : sp.tails ? '3×6' : '2×6'
    const rafSp = raf === '2×6' ? 16 * IN : 2
    rows.push({ part: 'Ledger', size: '2×8', qty: 1, lengthFt: W })
    if (sp.form !== 'concealed') rows.push({ part: 'Beam', size: sp.form === 'portico' ? '6×8' : sp.beam.replace('x', '×'), qty: 1, lengthFt: W - 1.5 * IN })
    rows.push({ part: 'Rafters', size: raf, qty: nRafters(W, rafSp, 1.5 * IN) * (sp.form === 'concealed' ? 2 : 1), lengthFt: P * slope })
    if (!sp.trellis) rows.push({ part: 'Decking', size: '1×6 T&G cedar', qty: 1, note: `${(W * P * slope).toFixed(1)} SF` })
    if (!sp.tails) rows.push({ part: 'Fascia + rake boards', size: '2×8', qty: 1, lengthFt: W + 2 * P })
    if (sp.form === 'concealed') {
      rows.push({ part: 'Knife plates', size: '3/8″ steel, lagged to each stud', qty: nRafters(W, 16 * IN, 1.5 * IN) })
      rows.push({ part: 'Soffit', size: '3/4″ ply or T&G', qty: 1, note: `${(W * P).toFixed(1)} SF` })
    }
    roofRow(W * P * slope)
    if (sp.gutter) rows.push({ part: 'Gutter', size: gutterLabel(sp), qty: 1, lengthFt: W + 4 * IN, note: sp.downspouts ? 'one downspout' : undefined })
  }
  if (sp.form === 'portico') {
    rows.push({ part: 'Posts', size: sp.postSize.replace('x', '×'), qty: 2, lengthFt: sp.zUnder - sp.zFloor })
    rows.push({ part: 'Knee braces', size: '4×4', qty: 2 })
    rows.push({ part: 'Post bases', size: 'standoff, SS', qty: 2 })
  } else if (sp.form === 'shed' || sp.form === 'gable') {
    const n = sp.stations.length
    const label = AWNING_BRACKETS.find((b) => b.key === sp.bracket)?.label ?? 'Bracket'
    rows.push({ part: 'Brackets', size: label + (sp.bracket === 'knee' || sp.bracket === 'curved' ? ` — ${sp.bracketSize.replace('x', '×')}` : ''), qty: n })
    rows.push({ part: 'Bracket bolts', size: sp.bracket === 'timber' ? '3/4″ SS, 3″ sq. washers' : '1/2″ 316 SS, plugged', qty: n * (sp.bracket === 'strut' ? 3 : 4) })
    rows.push({ part: 'Blocking', size: '2× behind each leg + the ledger', qty: n + 1 })
  }
  rows.push({ part: 'Ledger fasteners', size: 'SS LedgerLOK, each stud', qty: 2 * (Math.ceil(W / (16 * IN)) + 1) })
  return rows
}

/** how far the awning reaches past its span ends (gable/portico rake overhang) and out from the
 *  wall face (its full projection incl. gutter) — plan footprint for snap, pick and preview */
export function awningFootprintSU(spec: AwningSpec): { s0: number; s1: number; u0: number; u1: number } {
  const wide = spec.form === 'gable' || (spec.form === 'portico' && spec.porticoRoof === 'gable')
  const rake = wide ? 8 * IN : 0
  const front = spec.form === 'fabric' ? 0.1 : spec.gutter ? 6 * IN : 1 * IN
  return { s0: spec.s0 - rake, s1: spec.s1 + rake, u0: 0, u1: spec.projectionFt + (wide ? 6 * IN : 0) + front }
}
